/**
 * Centralized File Upload Configuration
 *
 * This file contains all standardized file type configurations used across the application
 * for file uploads, drag & drop, and file validation.
 */

import api from './axios';

// Maximum file size for images (2 MB)
export const MAX_IMAGE_SIZE = 2048 * 1024;

// Maximum filename length — must match MAX_ORIGINAL_FILENAME_LENGTH in backend/routers/files.py
// and MAX_FILENAME_LENGTH in backend/services/cab/cab_service.py
export const MAX_FILENAME_LENGTH = 200;

// Image file extensions
export const IMAGE_EXTENSIONS = [
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.webp',
  '.svg'
] as const;

// Document file extensions
export const DOCUMENT_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.ppt',
  '.pptx',
  '.txt',
  '.md',
  '.json',
  '.csv',
  '.xls',
  '.xlsx',
  '.html'
] as const;

// All supported file extensions (derived from IMAGE_EXTENSIONS + DOCUMENT_EXTENSIONS)
export const SUPPORTED_FILE_EXTENSIONS = [
  ...IMAGE_EXTENSIONS,
  ...DOCUMENT_EXTENSIONS
] as const;

// MIME types for images
export const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml'
] as const;

// MIME types for documents
export const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  'application/vnd.ms-powerpoint', // .ppt
  'text/plain', // .txt
  'text/markdown', // .md
  'application/json', // .json
  'text/csv', // .csv
  'application/vnd.ms-excel', // .xls
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'text/html' // .html
] as const;

// All supported MIME types
export const ALL_SUPPORTED_MIME_TYPES = [
  ...IMAGE_MIME_TYPES,
  ...DOCUMENT_MIME_TYPES
] as const;

// Accept attribute string for file inputs (all formats)
export const FILE_INPUT_ACCEPT = SUPPORTED_FILE_EXTENSIONS.join(',');

// Accept attribute string for file inputs (images only)
export const IMAGE_INPUT_ACCEPT = IMAGE_EXTENSIONS.join(',');

// Accept attribute string for file inputs (documents only)
export const DOCUMENT_INPUT_ACCEPT = DOCUMENT_EXTENSIONS.join(',');

// Human-readable format lists for error messages
export const SUPPORTED_IMAGE_FORMATS = 'PNG, JPG, JPEG, GIF, WEBP, SVG';
export const SUPPORTED_DOCUMENT_FORMATS = 'PDF, DOCX, DOC, PPTX, PPT, TXT, MD, JSON, CSV, XLS, XLSX, HTML';
export const SUPPORTED_ALL_FORMATS = `${SUPPORTED_IMAGE_FORMATS}, ${SUPPORTED_DOCUMENT_FORMATS}`;

/**
 * Check if a file is an image based on MIME type
 */
export const isImageFile = (file: File): boolean => {
  return IMAGE_MIME_TYPES.includes(file.type as any);
};

/**
 * Check if a file is a document based on MIME type
 */
export const isDocumentFile = (file: File): boolean => {
  return DOCUMENT_MIME_TYPES.includes(file.type as any);
};

/**
 * Check if a file extension is supported
 */
export const isSupportedExtension = (filename: string): boolean => {
  const extension = filename.toLowerCase().match(/\.[^.]+$/)?.[0];
  return extension ? SUPPORTED_FILE_EXTENSIONS.includes(extension as any) : false;
};

/**
 * Check if a file is an image based on extension
 */
export const isImageExtension = (filename: string): boolean => {
  const extension = filename.toLowerCase().match(/\.[^.]+$/)?.[0];
  return extension ? IMAGE_EXTENSIONS.includes(extension as any) : false;
};

/**
 * Check if a file is a document based on extension
 */
export const isDocumentExtension = (filename: string): boolean => {
  const extension = filename.toLowerCase().match(/\.[^.]+$/)?.[0];
  return extension ? DOCUMENT_EXTENSIONS.includes(extension as any) : false;
};

/**
 * Validate if a file is supported (checks both MIME type and extension)
 */
export const isFileSupported = (file: File): boolean => {
  const mimeTypeSupported = ALL_SUPPORTED_MIME_TYPES.includes(file.type as any);
  const extensionSupported = isSupportedExtension(file.name);
  return mimeTypeSupported || extensionSupported;
};

/**
 * Validate image file size
 */
export const isImageSizeValid = (file: File): boolean => {
  return file.size <= MAX_IMAGE_SIZE;
};

/**
 * Get file extension from filename
 */
export const getFileExtension = (filename: string): string => {
  const match = filename.toLowerCase().match(/\.[^.]+$/);
  return match ? match[0] : '';
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i)) + ' ' + sizes[i];
};

// ─── CAB-specific allowed extensions (matches ALL_ALLOWED_EXTENSIONS in cab_models.py) ───
export const CAB_ALLOWED_EXTENSIONS = new Set([
  // Documents
  '.pdf', '.docx', '.doc', '.pptx', '.ppt', '.xlsx', '.xls', '.csv', '.md', '.txt', '.html',
  // Source code
  '.py', '.js', '.ts', '.jsx', '.tsx', '.java', '.cs', '.cpp', '.c', '.go', '.rs', '.rb',
  '.php', '.swift', '.kt', '.json', '.xml', '.yaml', '.yml', '.sql', '.css', '.scss',
  '.vue', '.svelte',
  // Archives
  '.zip', '.tar', '.gz',
  // Images
  '.png', '.jpg', '.jpeg', '.gif',
]);

/**
 * Validate files before uploading to the general files/images endpoints.
 * Mirrors the backend checks in routers/files.py:
 *   1. Extension must be in SUPPORTED_FILE_EXTENSIONS (is_allowed_file)
 *   2. Filename must be <= MAX_FILENAME_LENGTH
 * Returns an array of error strings (one per rejected file).
 * Call BEFORE issuing any network requests to give instant feedback.
 */
export const validateFilesForUpload = (files: File[]): string[] => {
  const errors: string[] = [];
  for (const file of files) {
    const ext = (file.name.match(/\.[^.]+$/) ?? [''])[0].toLowerCase();
    if (!SUPPORTED_FILE_EXTENSIONS.includes(ext as any)) {
      errors.push(
        `"${file.name}" — file type "${ext || '(none)'}" is not supported. Allowed: ${SUPPORTED_ALL_FORMATS}.`
      );
    } else if (file.name.length > MAX_FILENAME_LENGTH) {
      errors.push(
        `"${file.name.slice(0, 40)}…" — filename is too long (${file.name.length} chars). Maximum is ${MAX_FILENAME_LENGTH} characters.`
      );
    }
  }
  return errors;
};

// ─── Build-mode attachment policy ───────────────────────────────────────────
// Build mode accepts a wider set of files (code, archives, fonts, media) with a
// 250 MB cap. The list lives in backend/config/file_types.json and is fetched
// from the backend, so the chat and the upload endpoint can't drift apart.
// Everything above stays as-is for Prototype mode, TAP and the other uploads.

export type BuildAttachmentCategory = 'document' | 'text' | 'image' | 'archive' | 'font' | 'audio' | 'video';

export interface BuildUploadPolicy {
  max_image_bytes: number;
  max_file_bytes: number;
  text_sniff_bytes: number;
  categories: Record<BuildAttachmentCategory, { label: string; extensions: string[] }>;
  blocked_extensions: string[];
  blocked_env_exceptions: string[];
}

let buildPolicyRequest: Promise<BuildUploadPolicy | null> | null = null;

/**
 * The Build-mode attachment policy, fetched once per session. Resolves null when
 * it can't be loaded; the upload endpoint enforces the same policy, so files
 * are still checked there.
 */
export const fetchBuildUploadPolicy = (projectId: string): Promise<BuildUploadPolicy | null> => {
  if (!buildPolicyRequest) {
    buildPolicyRequest = api
      .get<BuildUploadPolicy>(`/projects/${projectId}/files/upload-policy`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      })
      .then(res => res.data)
      .catch(error => {
        console.warn('Could not load the Build upload policy; relying on the server-side checks.', error);
        buildPolicyRequest = null; // try again on the next upload
        return null;
      });
  }
  return buildPolicyRequest;
};

/** Extension matched on the full suffix, so "site.tar.gz" is ".tar.gz", not ".gz". */
export const getBuildExtension = (filename: string, policy: BuildUploadPolicy): string => {
  const name = filename.toLowerCase();
  const listed = Object.values(policy.categories)
    .flatMap(category => category.extensions)
    .sort((a, b) => b.length - a.length);
  return listed.find(ext => name.endsWith(ext) && name.length > ext.length) ?? getFileExtension(name);
};

/** The file's Build category, or null for an unlisted extension (accepted only if it's plain text). */
export const getBuildCategory = (filename: string, policy: BuildUploadPolicy): BuildAttachmentCategory | null => {
  const ext = getBuildExtension(filename, policy);
  const entries = Object.entries(policy.categories) as [BuildAttachmentCategory, { extensions: string[] }][];
  return entries.find(([, category]) => category.extensions.includes(ext))?.[0] ?? null;
};

/** Why a filename is refused outright, or null. Mirrors get_blocked_reason in backend/config/file_types.py. */
export const getBlockedReason = (filename: string, policy: BuildUploadPolicy): string | null => {
  const name = (filename.toLowerCase().split(/[\\/]/).pop() ?? '');
  if (name === '.env' || (name.startsWith('.env.') && !policy.blocked_env_exceptions.includes(name))) {
    return 'environment files can hold secrets and uploads are served publicly';
  }
  const ext = getFileExtension(name);
  return ext && policy.blocked_extensions.includes(ext) ? `${ext} files can't be uploaded` : null;
};

/**
 * The upload endpoint's plain-text rule: the first bytes are valid UTF-8 with no
 * NUL bytes. Lets an unlisted file (Dockerfile, LICENSE, a lock file) through.
 */
export const looksLikeTextFile = async (file: File, sniffBytes: number): Promise<boolean> => {
  const bytes = new Uint8Array(await file.slice(0, sniffBytes).arrayBuffer());
  if (bytes.includes(0)) return false;
  try {
    // stream: a multi-byte character cut off by the slice isn't an error.
    new TextDecoder('utf-8', { fatal: true }).decode(bytes, { stream: file.size > sniffBytes });
    return true;
  } catch {
    return false;
  }
};

export interface BuildUploadCheck {
  /** Go to the images endpoint. */
  images: File[];
  /** Go to the files endpoint. */
  files: File[];
  /** One message per rejected file. */
  errors: string[];
}

/**
 * Sorts Build-mode uploads by extension (images to the images endpoint, the rest
 * to the files endpoint) and rejects what the endpoint would reject, before any
 * bytes are sent. Without a policy, files are routed by the default image list
 * and left for the server to check.
 */
export const checkBuildUploads = async (files: File[], policy: BuildUploadPolicy | null): Promise<BuildUploadCheck> => {
  const result: BuildUploadCheck = { images: [], files: [], errors: [] };

  for (const file of files) {
    if (file.name.length > MAX_FILENAME_LENGTH) {
      result.errors.push(`"${file.name.slice(0, 40)}…" — filename is too long (${file.name.length} chars). Maximum is ${MAX_FILENAME_LENGTH} characters.`);
      continue;
    }

    if (!policy) {
      if (!isImageExtension(file.name)) {
        result.files.push(file);
      } else if (isImageSizeValid(file)) {
        result.images.push(file);
      } else {
        result.errors.push(`"${file.name}" is ${formatFileSize(file.size)}; images can be up to ${formatFileSize(MAX_IMAGE_SIZE)}.`);
      }
      continue;
    }

    const blocked = getBlockedReason(file.name, policy);
    if (blocked) {
      result.errors.push(`"${file.name}" can't be uploaded: ${blocked}.`);
      continue;
    }

    const category = getBuildCategory(file.name, policy);
    if (category === 'image') {
      if (file.size > policy.max_image_bytes) {
        result.errors.push(`"${file.name}" is ${formatFileSize(file.size)}; images can be up to ${formatFileSize(policy.max_image_bytes)}.`);
      } else {
        result.images.push(file);
      }
      continue;
    }

    if (file.size > policy.max_file_bytes) {
      result.errors.push(`"${file.name}" is ${formatFileSize(file.size)}; files can be up to ${formatFileSize(policy.max_file_bytes)}.`);
      continue;
    }
    if (category === null && !(await looksLikeTextFile(file, policy.text_sniff_bytes))) {
      result.errors.push(`"${file.name}" isn't a supported file type. Documents, code and text, images, archives, fonts, audio and video are supported.`);
      continue;
    }
    result.files.push(file);
  }

  return result;
};

/**
 * Validate files before uploading to the CAB endpoint.
 * Checks both extension and filename length, mirroring cab_service.py.
 * Returns an array of error strings (one per rejected file).
 */
export const validateCABFilesForUpload = (files: File[]): string[] => {
  const errors: string[] = [];
  for (const file of files) {
    const ext = (file.name.match(/\.[^.]+$/) ?? [''])[0].toLowerCase();
    if (!CAB_ALLOWED_EXTENSIONS.has(ext)) {
      errors.push(
        `"${file.name}" — file type "${ext || '(none)'}" is not supported for CAB upload.`
      );
    } else if (file.name.length > MAX_FILENAME_LENGTH) {
      errors.push(
        `"${file.name.slice(0, 40)}…" — filename is too long (${file.name.length} chars). Maximum is ${MAX_FILENAME_LENGTH} characters.`
      );
    }
  }
  return errors;
};
